import { useEffect, useState } from "react";
import { ApiError, api } from "../../lib/api";
import { Button } from "../ui/button";
import { Field, Input, Select } from "../ui/input";
import { Modal } from "../ui/modal";
import { CRM_SECTOR_LABELS, dateTimeLocalToIso, toDateTimeLocalValue } from "../../lib/crm";
import { useToastStore } from "../../stores/toast";
import type { CrmAccount, CrmSector, Tenant } from "@shared/types";

type Mode = "link" | "create";

interface ActivateDialogProps {
  open: boolean;
  account: CrmAccount;
  onClose: () => void;
  onActivated: () => void;
}

export function ActivateDialog({ open, account, onClose, onActivated }: ActivateDialogProps) {
  const push = useToastStore((s) => s.push);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [mode, setMode] = useState<Mode>("link");
  const [tenantId, setTenantId] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [tenantType, setTenantType] = useState<CrmSector>("custom");
  const [activationDate, setActivationDate] = useState("");
  const [saving, setSaving] = useState(false);

  // Réouverture = nouveau départ : le nom et le métier sont repris du dossier,
  // la date d'activation retombe sur aujourd'hui.
  useEffect(() => {
    if (!open) return;
    setMode("link");
    setTenantId("");
    setTenantName(account.name);
    setTenantType(account.sector);
    setActivationDate(toDateTimeLocalValue(Date.now()).slice(0, 10));
    api<Tenant[]>("/api/tenants")
      .then(setTenants)
      .catch(() => setTenants([]));
  }, [open, account.name, account.sector]);

  const activate = async () => {
    if (saving) return;
    if (mode === "link" && !tenantId) {
      push("danger", "Choisis un établissement à lier");
      return;
    }
    if (mode === "create" && !tenantName.trim()) {
      push("danger", "Le nom de l'établissement est requis");
      return;
    }
    // `activatedAt` est optionnel côté schéma (jamais null) : on l'omet si vide.
    const activatedAt = dateTimeLocalToIso(activationDate ? `${activationDate}T12:00` : "");
    setSaving(true);
    try {
      await api(`/api/crm/accounts/${account.id}/activate`, {
        method: "POST",
        body: JSON.stringify(
          mode === "link"
            ? { mode, tenantId, ...(activatedAt ? { activatedAt } : {}) }
            : {
                mode,
                tenantName: tenantName.trim(),
                tenantType,
                ...(activatedAt ? { activatedAt } : {}),
              },
        ),
      });
      push("success", "Dossier activé", "L'établissement est lié au dossier.");
      onActivated();
    } catch (e) {
      const code = e instanceof ApiError ? e.code : "";
      if (code === "already_linked") {
        push(
          "danger",
          "Dossier déjà lié",
          "Un autre établissement est déjà rattaché à ce dossier.",
        );
      } else if (code === "tenant_not_found") {
        push("danger", "Établissement introuvable");
      } else {
        push("danger", "Échec de l'activation");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Activer le dossier"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={() => void activate()} disabled={saving}>
            {saving ? "Activation…" : "Activer"}
          </Button>
        </>
      }
    >
      <div className="crm-form">
        <div className="crm-form__row">
          <label className="checkbox-row">
            <input
              type="radio"
              name="activate-mode"
              checked={mode === "link"}
              onChange={() => setMode("link")}
            />
            <span>Lier un établissement existant</span>
          </label>
          <label className="checkbox-row">
            <input
              type="radio"
              name="activate-mode"
              checked={mode === "create"}
              onChange={() => setMode("create")}
            />
            <span>Créer l'établissement</span>
          </label>
        </div>

        <div className="crm-form__grid">
          {mode === "link" ? (
            <Field
              label="Établissement"
              hint={
                tenants.length === 0
                  ? "Aucun établissement disponible — crée-le plutôt."
                  : undefined
              }
            >
              <Select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                <option value="">Choisir…</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <>
              <Field label="Nom de l'établissement">
                <Input value={tenantName} onChange={(e) => setTenantName(e.target.value)} />
              </Field>
              <Field label="Métier" hint="Détermine le prompt et les outils de l'agent vocal.">
                <Select
                  value={tenantType}
                  onChange={(e) => setTenantType(e.target.value as CrmSector)}
                >
                  {Object.entries(CRM_SECTOR_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          )}
          <Field label="Date d'activation">
            <Input
              type="date"
              value={activationDate}
              onChange={(e) => setActivationDate(e.target.value)}
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
